import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import TextInput from "../../components/forms/TextInput/TextInput.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import ShareRow from "../../components/share/ShareRow.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";
import { breadcrumbSchema } from "../../seo/schema.js";
import { REJECT_CODES } from "../../data/rejectCodes.js";
import { REJECT_SECTIONS, SECTION_ORDER } from "./rejectSections.js";

/**
 * Hub for the IRS reject-code library: every ACTIVE rule in the TY2026 Form
 * 2290 Business Rules, grouped by the part of the return it's about. Rules a
 * filer can fix link to their own page; rules only support can resolve
 * (computed totals, transmission/XML, signature mechanics) are listed with
 * their explanation and no page of their own - a page for each would be thin.
 */
const meta = ROUTE_META["/form-2290-reject-codes"];

const matches = (r, q) =>
  !q || `${r.code} ${r.title} ${r.explanation} ${r.irsText}`.toLowerCase().includes(q.toLowerCase().trim());

const RejectCodesHub = () => {
  const [q, setQ] = useState("");
  const fixable = REJECT_CODES.filter((r) => r.fixable && matches(r, q));
  const support = REJECT_CODES.filter((r) => !r.fixable && matches(r, q));
  const total = REJECT_CODES.length;
  const fixableTotal = REJECT_CODES.filter((r) => r.fixable).length;

  return (
    <div className="max-w-[1000px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/form-2290-reject-codes"
        keywords={meta.keywords}
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Form 2290 reject codes", path: "/form-2290-reject-codes" }])]}
      />
      <PageHeader eyebrow="IRS Form 2290 Business Rules · TY2026" title="Form 2290 Reject Codes and How to Fix Them">
        When the IRS rejects an e-filed Form 2290, it returns a rule code. Here are all {total} active rules for the
        2026–2027 period, in the IRS's own words, with what each one means. {fixableTotal} of them are things you can
        fix yourself — each has a step-by-step fix. The rest are system checks our support team resolves.
      </PageHeader>

      <div className="relative mt-7 max-w-[460px]">
        <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <TextInput
          className="pl-9"
          placeholder="Search a code, e.g. R0000-922-01 or VIN"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search reject codes"
        />
      </div>

      {SECTION_ORDER.map((key) => {
        const rows = fixable.filter((r) => r.section === key);
        if (rows.length === 0) return null;
        return (
          <section key={key} className="mt-9">
            <h2 className="font-disp text-[22px]">{REJECT_SECTIONS[key].label}</h2>
            <div className="grid gap-2.5 mt-3">
              {rows.map((r) => (
                <Panel key={r.code} padded className="hover:border-[var(--accent)] transition-colors">
                  <Link to={`/form-2290-reject-codes/${r.codeSlug}`} className="no-underline text-inherit">
                    <b className="font-mono text-[14px]">{r.code}</b> · <b>{r.title}</b>
                    <p className="text-[var(--muted)] text-sm mt-1">{r.explanation}</p>
                  </Link>
                </Panel>
              ))}
            </div>
          </section>
        );
      })}

      <Panel padded className="!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between gap-5 mt-10">
        <div>
          <b className="font-disp text-[18px]">Got a rejection on a return you filed here?</b>
          <p className="text-[var(--muted)] text-sm mt-1 max-w-[50ch]">It's on the return in your dashboard, with the fix and a link to the step.</p>
        </div>
        <Link to="/dashboard">
          <Button>
            Open my returns <ArrowRight size={16} />
          </Button>
        </Link>
      </Panel>

      {support.length > 0 && (
        <section className="mt-12">
          <h2 className="font-disp text-[22px]">System checks our support team resolves</h2>
          <p className="text-[var(--muted)] mt-2 max-w-[75ch]">
            These cover figures the return computes, the e-file format, transmission, and details you can't edit
            in the app, so they need our support team rather than a change on your side —{" "}
            <Link to="/contact" className="text-[var(--accent)]">contact support</Link> with the code.
          </p>
          <div className="overflow-x-auto mt-4">
            <table className="w-full border-collapse text-sm min-w-[560px]">
              <thead>
                <tr>
                  {["Code", "What it means"].map((h) => (
                    <th key={h} className="text-left p-2.5 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {support.map((r) => (
                  <tr key={r.code}>
                    <td className="p-2.5 border-b border-[var(--line)] font-mono whitespace-nowrap align-top">{r.code}</td>
                    <td className="p-2.5 border-b border-[var(--line)] align-top">
                      <b>{r.title}</b>
                      <div className="text-[var(--muted)] mt-0.5">{r.explanation}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {fixable.length === 0 && support.length === 0 && (
        <p className="mt-8 text-[var(--muted)]">No active Form 2290 rule matches "{q}".</p>
      )}

      <Panel padded className="flex flex-wrap items-center justify-between gap-5 mt-12">
        <div>
          <b className="font-disp text-[17px]">Rejected somewhere else?</b>
          <p className="text-[var(--muted)] text-sm mt-1 max-w-[50ch]">
            File again here. Returns are checked against the IRS e-file schema and key business rules before they're
            sent. Read <Link to="/blog/form-2290-rejected-common-reasons" className="text-[var(--accent)]">the most common reasons Form 2290 is rejected</Link>.
          </p>
        </div>
        <Link to="/login?tab=up">
          <Button>
            Create your free account <ArrowRight size={16} />
          </Button>
        </Link>
      </Panel>

      <ShareRow path="/form-2290-reject-codes/" title="Form 2290 rejected? Every IRS reject code with how to fix it:" />

      <p className="text-[var(--muted)] text-xs mt-11 pt-5 border-t border-[var(--line)] max-w-[70ch]">
        Rule codes and text from the IRS Form 2290 Business Rules Publication (TY2026 v1.1), active rules only.
        Explanations are EasyFile2290's. General information, not tax advice for your situation.
      </p>
    </div>
  );
};

export default RejectCodesHub;
